/**
 * Lightweight CSV parser that handles quoted fields (commas inside double-quotes).
 * No external dependencies.
 */
export function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const lines = splitCsvLines(text);
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = parseCsvLine(lines[0]);
  const rows: string[][] = [];

  for (let i = 1; i < lines.length; i++) {
    const row = parseCsvLine(lines[i]);
    // Skip empty rows (all cells empty or blank)
    if (row.every(cell => cell === '')) continue;
    rows.push(row);
  }

  return { headers, rows };
}

/** Split raw text into logical CSV lines, respecting quoted fields that span newlines. */
function splitCsvLines(text: string): string[] {
  const lines: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      // Handle \r\n
      if (ch === '\r' && text[i + 1] === '\n') i++;
      if (current.trim() !== '') lines.push(current);
      current = '';
    } else {
      current += ch;
    }
  }

  if (current.trim() !== '') lines.push(current);
  return lines;
}

/** Parse a single CSV line into cells, handling quoted fields. */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (inQuotes) {
      if (ch === '"') {
        // Escaped quote ("") or end of quoted field
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        cells.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
  }

  cells.push(current.trim());
  return cells;
}
